import {
  SITUACAO_KEY_ACORDO_IA,
  SITUACAO_KEY_ACORDO_MANUAL,
  SITUACAO_KEY_SEM_RESPOSTA,
  TIPOS_ENCERRAMENTO_COM_ACORDO,
} from '@/features/negociacoes';

import type { NegociacaoCasoDetail } from '../types/negociacaoDetailTypes';
import type {
  OfertaNotificacaoCaso,
  PrefillNotificacaoCaso,
} from '../types/ofertaNotificacaoTypes';

// Tipo gravado antes de existirem ACORDO_MANUAL e ACORDO_IA; ainda há casos com ele em produção.
const TIPO_ENCERRAMENTO_ACORDO_LEGADO = 'ACORDO';

export function hasSituacaoSemResposta(caso: Pick<NegociacaoCasoDetail, 'situacoes'>): boolean {
  return caso.situacoes.some((situacao) => situacao.key === SITUACAO_KEY_SEM_RESPOSTA);
}

function registraAcordo(caso: OfertaNotificacaoCaso): boolean {
  const tipo = caso.encerramento?.tipo;
  if (tipo === TIPO_ENCERRAMENTO_ACORDO_LEGADO) return true;
  if (tipo != null && TIPOS_ENCERRAMENTO_COM_ACORDO.has(tipo)) return true;
  return caso.situacoes.some(
    (situacao) =>
      situacao.key === SITUACAO_KEY_ACORDO_MANUAL || situacao.key === SITUACAO_KEY_ACORDO_IA,
  );
}

/**
 * Caso ausente (ainda carregando ou consulta que falhou) nunca autoriza a oferta. As comparações
 * estritas são de propósito: campo que não veio na resposta é "não sei", e "não sei" nega. O
 * schema de resposta do core descarta chave não declarada — foi assim que `archivedAt` sumiu do
 * detalhe e a oferta deixou de aparecer em todo caso (core-api, getCasoByIdResponseSchema).
 */
export function shouldOfferNotificacao(caso: OfertaNotificacaoCaso | null | undefined): boolean {
  if (!caso) return false;
  if (
    (caso.status !== 'CLOSED' && caso.status !== 'NO_AGREEMENT') ||
    caso.notificacaoGeradaItemId !== null ||
    caso.archivedAt !== null ||
    caso.atendimentoHumanoAtivo !== false
  ) {
    return false;
  }

  return !registraAcordo(caso) && hasSituacaoSemResposta(caso);
}

function buildNotificacaoPrefillQuery(caso: PrefillNotificacaoCaso): string {
  const params = new URLSearchParams({ negociacaoCasoId: caso.id, method: 'DIGITAL' });
  if (caso.counterpartyName) params.set('name', caso.counterpartyName);
  if (caso.counterpartyCpfCnpj) params.set('doc', caso.counterpartyCpfCnpj);
  if (caso.counterpartyEmail) params.set('email', caso.counterpartyEmail);
  if (caso.counterpartyPhone) params.set('phone', caso.counterpartyPhone);
  if (caso.claimAmount != null) params.set('valor', String(caso.claimAmount));
  return params.toString();
}

/** Destino da oferta nas duas telas: a criação individual já preenchida com o devedor do caso. */
export function buildOfertaNotificacaoHref(caso: PrefillNotificacaoCaso): string {
  return `/notificacoes/criar/individual?${buildNotificacaoPrefillQuery(caso)}`;
}
